import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import Logo from '@/components/Logo';
import { supabase } from '@/integrations/supabase/client';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [type, setType] = useState<string | null>(null);
  const [hasValidSession, setHasValidSession] = useState<boolean>(false);
  const [isCheckingSession, setIsCheckingSession] = useState<boolean>(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Extract token from URL parameters with enhanced detection
  useEffect(() => {
    // More comprehensive token detection
    const extractToken = () => {
      // Log full URL for debugging (redacting sensitive parts)
      const urlForLogging = window.location.href.replace(/token=([^&]+)/, 'token=[REDACTED]')
        .replace(/access_token=([^&]+)/, 'access_token=[REDACTED]');

      console.group('🔍 Reset Password URL Analysis');
      console.log('Current URL (redacted):', urlForLogging);
      console.log('Path:', window.location.pathname);
      console.log('Search:', window.location.search);
      console.log('Hash:', window.location.hash);

      // Check query parameters
      const searchParams = new URLSearchParams(location.search);
      console.log('Query parameters:', Object.fromEntries(searchParams.entries()));

      // Try known parameter names in query string
      const queryToken = searchParams.get('token') ||
                         searchParams.get('access_token') ||
                         searchParams.get('t') ||
                         searchParams.get('key');

      if (queryToken) {
        console.log('✅ Token found in query parameters');
        console.groupEnd();
        return queryToken;
      }

      // Check hash fragment (for SPA redirects)
      let hashToken = null;
      if (window.location.hash) {
        // Format: #access_token=xxx&refresh_token=yyy
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        console.log('Hash parameters:', Object.fromEntries(hashParams.entries()));

        hashToken = hashParams.get('access_token') ||
                    hashParams.get('token') ||
                    hashParams.get('t');

        if (hashToken) {
          console.log('✅ Token found in hash fragment');
          console.groupEnd();
          return hashToken;
        }

        // Some implementations might format it differently
        const accessTokenMatch = window.location.hash.match(/access_token=([^&]+)/);
        if (accessTokenMatch && accessTokenMatch[1]) {
          console.log('✅ Token found via regex in hash');
          console.groupEnd();
          return accessTokenMatch[1];
        }
      }

      // Check if the token is in Supabase's session storage
      const getSupabaseStorageToken = () => {
        try {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.includes('supabase.auth.token')) {
              const storedData = localStorage.getItem(key);
              if (storedData) {
                const parsed = JSON.parse(storedData);
                console.log('Found Supabase auth storage with access token');
                return parsed?.access_token || null;
              }
            }
          }
        } catch (e) {
          console.error('Error checking local storage:', e);
        }
        return null;
      };

      const storageToken = getSupabaseStorageToken();
      if (storageToken) {
        console.log('✅ Token found in local storage');
        console.groupEnd();
        return storageToken;
      }

      // Check if we're in an OAuth callback that Supabase can handle
      const isOAuthCallback = window.location.search.includes('code=') ||
                             window.location.hash.includes('code=');

      if (isOAuthCallback) {
        console.log('📌 This appears to be an OAuth callback URL');
      }

      console.log('❌ No token found in URL parameters or storage');
      console.groupEnd();
      return null;
    };

    // Check if we have a valid session (for password recovery)
    const checkSupabaseSession = async () => {
      try {
        setIsCheckingSession(true);
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          console.log('✅ Supabase session found, can use for password reset');
          setHasValidSession(true);
          setAccessToken(session.access_token);
          // Clear any previous errors since we have a valid session
          setError('');
        } else {
          console.log('❌ No valid Supabase session found');
          setHasValidSession(false);
        }
      } catch (err) {
        console.error('Error checking session:', err);
        setHasValidSession(false);
      } finally {
        setIsCheckingSession(false);
      }
    };

    const initializePasswordReset = async () => {
      const token = extractToken();
      const type = new URLSearchParams(location.search).get('type');

      if (token) {
        setAccessToken(token);
        setType(type);
        setHasValidSession(true);
        setIsCheckingSession(false);
        // Clear any errors
        setError('');
      } else {
        // No token found in URL, check if we have a valid session
        // (Supabase may have already processed the recovery token and created a session)
        await checkSupabaseSession();

        // Check session result - if no session found, show error
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setError('No reset token found in the URL. Please check your email link and try again.');
        }
      }
    };

    initializePasswordReset();
  }, [location]);

  // Password validation
  const validatePassword = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    setError('');
    return true;
  };

  // Handle password reset submission with enhanced token handling
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePassword()) {
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      console.group('Password Reset Attempt');
      
      if (!accessToken) {
        // Last attempt to get a token if we somehow missed it earlier
        const { data } = await supabase.auth.getSession();
        
        if (!data.session) {
          throw new Error('Missing reset token and no active session found. Please check your email and use the link provided.');
        }
        
        console.log('Using session for password reset instead of direct token');
      } else {
        console.log('Using detected token for password reset');
      }

      // First try to update the password using the active session
      const { error } = await supabase.auth.updateUser({
        password
      });

      if (error) {
        // If direct update fails, try the alternative method with token
        console.log('Direct password update failed, trying alternative method:', error.message);
        
        // Try using the token explicitly if available
        if (accessToken) {
          console.log('Attempting password reset with explicit token');
          
          // We may need to set the token in the auth session first
          try {
            const { error: setSessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: '',
            });
            
            if (setSessionError) {
              console.log('Failed to set session with token:', setSessionError.message);
            } else {
              // Try updating password again after setting session
              const { error: updateError } = await supabase.auth.updateUser({
                password
              });
              
              if (updateError) {
                throw updateError;
              }
            }
          } catch (sessionError) {
            console.error('Error setting session:', sessionError);
            throw error; // Throw the original error if this approach fails
          }
        } else {
          throw error;
        }
      }
      
      console.log('Password reset successful');
      console.groupEnd();

      // Password reset successful
      setIsSuccess(true);
      toast({
        title: 'Password updated successfully',
        description: 'Your password has been changed. You can now log in with your new password.',
      });

      // Redirect to login page after 3 seconds
      setTimeout(() => {
        navigate('/auth', { replace: true });
      }, 3000);

    } catch (err: any) {
      console.error('Password reset error:', err);
      console.groupEnd();
      
      setError(err.message || 'Failed to reset password. Please try again or request a new reset link.');
      toast({
        variant: "destructive",
        title: "Password reset failed",
        description: err.message || "An error occurred while setting your new password.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle navigation to login page
  const handleReturnToLogin = () => {
    navigate('/auth', { replace: true });
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/5 to-accent/20">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80')] bg-cover bg-center opacity-[0.03] mix-blend-overlay" />
      
      <div className="container relative flex flex-col items-center justify-center lg:px-0 animate-fade-in">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px] md:w-[450px]">
          <div className="flex flex-col items-center space-y-2 text-center mb-8">
            <Logo size="lg" className="mb-2" />
            <h1 className="text-2xl font-semibold tracking-tight">
              Reset Your Password
            </h1>
            <p className="text-sm text-muted-foreground max-w-xs">
              Enter your new password below to reset your account.
            </p>
          </div>

          {error && (
            <div className="bg-destructive/10 p-3 rounded-md flex items-start space-x-2">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <span className="text-sm text-destructive">{error}</span>
            </div>
          )}

          <Card className="border-none shadow-soft">
            {isSuccess ? (
              <CardContent className="pt-6 pb-6 flex flex-col items-center space-y-4">
                <div className="rounded-full bg-primary/10 p-3">
                  <CheckCircle2 className="h-8 w-8 text-primary" />
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-1">Password Reset Successful</h3>
                  <p className="text-sm text-muted-foreground">
                    Your password has been updated successfully. You will be redirected to the login page in a moment.
                  </p>
                </div>
                <Button 
                  className="w-full mt-4" 
                  onClick={handleReturnToLogin}
                >
                  Return to Login
                </Button>
              </CardContent>
            ) : (
              <CardContent className="pt-6">
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">New Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pr-10"
                        required
                        disabled={isLoading || isCheckingSession || !hasValidSession}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading || isCheckingSession || !hasValidSession}
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Eye className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="sr-only">
                          {showPassword ? 'Hide password' : 'Show password'}
                        </span>
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Must be at least 8 characters long
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="confirm-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pr-10"
                        required
                        disabled={isLoading || isCheckingSession || !hasValidSession}
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full relative"
                    disabled={isLoading || isCheckingSession || !hasValidSession}
                  >
                    <span className={isLoading ? "opacity-0" : "opacity-100"}>
                      Reset Password
                    </span>
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </div>
                    )}
                  </Button>
                  
                  <div className="text-center">
                    <Button 
                      variant="link" 
                      onClick={handleReturnToLogin}
                      className="text-xs"
                    >
                      Return to login
                    </Button>
                  </div>
                </form>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
